import { Rule } from '@travelnest/merlin.rule';
import { MarginWrapper } from '@travelnest/merlin.spacer';

import { cloneDeep, get, set } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { IEventV2 } from '../../../data/entities/event';
import { IBookingExtraItems, IBookingV2 } from '../../../data/entities/event/booking';
import { IAppliedPromotion } from '../../../data/entities/promotions';
import { IProperty } from '../../../data/entities/property';
import { channelFriendlyNames } from '../../../entities/contracts/constants';
import { currencySymbolForCurrencyString } from '../../helpers/currency_helpers';
import { breakdownHasSucceeded, calculateCSBookingFee, financesHaveDiscrepancy, TaxSubType, touristTaxAlertContent } from '../../pages/property/bookings/details/helpers/helpers';
import { FormInputSymbol } from '../form/form-input-symbol/FormInputSymbol';
import { FormInput } from '../form/form-input/FormInput';
import { FormLabel } from '../form/form-label/FormLabel';
import { FormRow } from '../form/form-row/FormRow';
import { Content } from '../page/content/Content';
import { Panel } from '../panel/Panel';
import { Tbody, Td } from '../table';
import { Table } from '../table/table/Table';
import { Th } from '../table/th/Th';
import { Thead } from '../table/thead/Thead';
import { Tr } from '../table/tr/Tr';
import { ExceptionalPayments } from './exceptional-payments/ExceptionalPaymentsContainer';
import { valuesConverted } from './helpers';

interface ICustomerServiceDetailsProps {
	event: IEventV2;
	property: IProperty;
	setBookingState: (event: IEventV2) => any;
}

export const CustomerServiceDetails: React.FC<ICustomerServiceDetailsProps> = (props) => {

	const UNDEFINED = 'UNDEFINED';
	const booking = props.event.booking || {} as IBookingV2;

	const external = get(booking, 'external_id[0]', { source: UNDEFINED, id: UNDEFINED });
	const channelName = channelFriendlyNames[props.event.source] || props.event.source;
	const breakdown = booking.value.breakdown;

	const originalOwnerRentValue = get(breakdown, 'owner.rent_value') as number;
	const [ownerRent, setOwnerRent] = useState(originalOwnerRentValue);

	const appliedPromotions = booking.owner_promotional_discounts || [];
	const totalDiscountAmount = appliedPromotions.reduce((partialSum: number, curr: IAppliedPromotion) => partialSum + curr.discount_amount!, 0);
	const discountedOwnerRent = ownerRent - totalDiscountAmount;

	const ownerPaymentValue = get(breakdown, 'owner.payment_value') as number;
	const bookingTotal = get(booking, 'value.total') as number;
	const wasConverted = valuesConverted(breakdown);

	const convertedFromBookingTotal = bookingTotal / get(breakdown, 'channel.converted_cash_receipt_fx_rate', 1) as number;

	const ownerExtras = get(breakdown, 'owner.extras.items', []) as IBookingExtraItems[];
	const cleaningExtra = ownerExtras.find((x) => x.name === 'cleaning fee');
	const cleaningFee = cleaningExtra ? cleaningExtra.amount : 0;

	const bookingFee = calculateCSBookingFee(bookingTotal, discountedOwnerRent, [cleaningFee]);

	const touristTax = get(breakdown, 'owner.converted_tourist_tax') as number;
	const touristTaxType = get(breakdown, 'owner.tourist_tax_type') as TaxSubType;

	const currencySymbol = currencySymbolForCurrencyString(booking.value.currency);
	const convertedFromCurrencySymbol = currencySymbolForCurrencyString(booking.value.breakdown.channel.cash_receipt_currency);
	const convertedCurrencySymbol = currencySymbolForCurrencyString(booking.value.breakdown.channel.converted_cash_receipt_currency);

	const conversionRate = get(breakdown, 'channel.converted_cash_receipt_fx_rate') as number;

	const breakdownSucceeded = breakdownHasSucceeded(breakdown);
	const hasDiscrepancy = financesHaveDiscrepancy(breakdown, bookingTotal || 0);

	const tnCommRate = get(breakdown, 'travelnest.commission_rate');
	const tnCommissionRate = tnCommRate === null ? undefined : (tnCommRate * 100).toFixed(2);

	const tnCommVal = get(breakdown, 'travelnest.commission_value');
	const tnCommissionValue = tnCommVal === null ? undefined : tnCommVal.toFixed(2);

	const tnCommVatVal = get(breakdown, 'travelnest.commission_vat_value');

	const tnVatRequiredText = tnCommVatVal ? '(including VAT)' : '(No VAT required)';

	const bookingFromHomeAwayOrDirect = props?.event?.source.startsWith('homeaway.co') || props?.event?.source.startsWith('direct');

	const [totalBookingValue, setTotalBookingValue] = useState(discountedOwnerRent + cleaningFee + (bookingFee ? bookingFee : 0));

	const onChange = (field: string, value: number) => {
		const next = cloneDeep(props.event);
		if (field === 'owner_rent') {
			if (sessionStorage.getItem('old_owner_rent_value') === null) {
				sessionStorage.setItem('old_owner_rent_value', currencySymbol + originalOwnerRentValue.toString());
			}
			sessionStorage.setItem('new_owner_rent_value', currencySymbol + value.toString());

			set(next, 'booking.value.breakdown.owner.rent_value', value);
			setOwnerRent(value);
			props.setBookingState(next);
		}
		if (field === 'total_booking_value') {
			if (sessionStorage.getItem('old_total_booking_value') === null) {
				sessionStorage.setItem('old_total_booking_value', convertedCurrencySymbol + totalBookingValue.toFixed(2).toString());
			}
			sessionStorage.setItem('new_total_booking_value', convertedCurrencySymbol + value.toString());

			set(next, 'booking.value.breakdown.channel.cash_receipt_value', value / conversionRate);
			setTotalBookingValue(value);
			props.setBookingState(next);
		}
	};

	// stripping out dependency on the marketing prefs service means we can't refer to the discount type in direct discounts
	// for now we make a best guess - if the discount amount is 1.0 or less, it's probably a percentage.
	const bestGuessDiscountType = (amount: number = 0) => amount <= 1 ? 'percentage' : 'fixed';

	const renderAppliedDiscount = (promotionsArr: IAppliedPromotion[]) => {
		return (promotionsArr || []).map((discount) => {
			const type = bestGuessDiscountType(discount.discount_amount);
			return <Tr>
				<Td className="booking-breakdown__table-cell--slight-wide">
					<MarginWrapper>
						{(discount.description || discount.internal_reference) + ' - ' + props.event.source}
					</MarginWrapper>
				</Td>
				{(!type || type !== 'fixed') &&
					<Td className="booking-breakdown__table-cell--narrow">
						<FormInput value={discount.discount_amount * 100 + '%'} readOnly />
					</Td>}
				<Td className="booking-breakdown__table-cell--narrow">
					<FormInput value={'-' + currencySymbol + discount.discount_amount} readOnly />
				</Td>
			</Tr>;
		});
	};

	return <Content type="full">
		<h2>Customer Success Breakdown</h2>
		{!breakdownSucceeded && <Panel type="default" withIcon={true}>There was a problem generating the booking breakdown</Panel>}
		<React.Fragment>
			{hasDiscrepancy && <Panel type="default">There is a discrepancy with this booking</Panel>}
			<FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="booking-list_property-name">Property Name</FormLabel>
						<FormInput id={'booking-list_property-name'} value={props.property.name} disabled />
					</div>
					<div>
						<FormLabel htmlFor="booking-list_property-id">Property ID</FormLabel>
						<FormInput id={'booking-list_property-id'} value={props.property.id} disabled />
					</div>
				</section>
			</FormRow>
			<FormRow>
				<section className="booking-list_side-by-side">
					<div>
						<FormLabel htmlFor="booking-list_owner-id">Owner ID</FormLabel>
						<FormInput id={'booking-list_owner-id'} value={props.property.owner_id} disabled />
					</div>
					<div>
						<FormLabel htmlFor="booking-list_booking-sources">{`${channelName} booking number`}</FormLabel>
						<FormInput id={'booking-list_booking-sources'} value={external ? external.id : UNDEFINED} disabled />
					</div>
				</section>
			</FormRow>
			{breakdownSucceeded && <>
				<Table className="booking-cs-internal__table">
					<Thead>
						<Tr>
							<Th>Item</Th>
							<Th>Value</Th>
						</Tr>
					</Thead>
					<Tbody>
						<Tr>
							<Td>Owner rent</Td>
							<Td><FormInputSymbol
								symbol={currencySymbol}
								value={ownerRent}
								readOnly={totalDiscountAmount > 0}
								min={0}
								placeholder={UNDEFINED}
								onChange={(e) => onChange('owner_rent', Number(e.target.value))} /></Td>
						</Tr>
						{renderAppliedDiscount(appliedPromotions)}
						{appliedPromotions.length !== 0 &&
							<Tr>
								<Td>Total discount</Td>
								<Td>
									<FormInputSymbol
										symbol={currencySymbol}
										value={totalDiscountAmount}
										readOnly={true}
										min={0}
										placeholder={UNDEFINED} />
								</Td>
							</Tr>}
						{appliedPromotions.length !== 0 &&
							<Tr>
								<Td>Discounted rent</Td>
								<Td>
									<FormInputSymbol
										symbol={currencySymbol}
										value={discountedOwnerRent}
										readOnly={true}
										min={0}
										placeholder={UNDEFINED} />
								</Td>
							</Tr>}
						<Tr>
							<Td>
								Cleaning fee
							</Td>
							<Td>
								<FormInputSymbol
									symbol={currencySymbol}
									value={cleaningFee.toFixed(2)}
									placeholder={UNDEFINED} readOnly />
							</Td>
						</Tr>
						<Tr>
							<Td>
								Booking fee
							</Td>
							<Td>
								<FormInputSymbol
									symbol={currencySymbol}
									value={(bookingFee || bookingFee === 0) ? bookingFee.toFixed(2) : undefined}
									placeholder={UNDEFINED} readOnly />
							</Td>
						</Tr>
						{
							!!touristTax ?
							<Tr>
								<Td>
									Tourist tax {!!touristTaxType && !!touristTaxAlertContent[touristTaxType].HEADER ? `(${touristTaxAlertContent[touristTaxType].HEADER})` : ''}
								</Td>
								<Td>
									<FormInputSymbol
										symbol={currencySymbol}
										value={touristTax.toFixed(2)}
										placeholder={UNDEFINED} readOnly />
								</Td>
							</Tr>
							:
							null
						}
						{
							bookingFromHomeAwayOrDirect ?
								<Tr>
									<Td><b>Total</b></Td>
									<Td><FormInputSymbol
										symbol={convertedCurrencySymbol}
										value={totalBookingValue.toFixed(2)}
										placeholder={UNDEFINED}
										onChange={(e) => onChange('total_booking_value', Number(e.target.value))} /></Td>
								</Tr>
								:
								<Tr>
									<Td><b>{wasConverted ? 'Guest Paid (Converted)' : 'Total'}</b></Td>
									<Td><FormInputSymbol
										symbol={currencySymbol}
										value={totalBookingValue.toFixed(2)}
										placeholder={UNDEFINED} readOnly /></Td>
								</Tr>
						}
						{
							wasConverted ?
								<>
									<Tr>
										<Td>Foreign Exchange Rate</Td>
										<Td><FormInputSymbol symbol={''} value={conversionRate} readOnly /></Td>
									</Tr>
									<Tr>
										<Td>Guest Paid</Td>
										<Td><FormInputSymbol
											symbol={convertedFromCurrencySymbol}
											value={(bookingFee || bookingFee === 0) ? convertedFromBookingTotal.toFixed(2) : undefined}
											placeholder={UNDEFINED} readOnly /></Td>
									</Tr>
								</>
								:
								null
						}
					</Tbody>
				</Table>
			</>
			}

			<h2>Commission</h2>
			<FormRow>
				<FormLabel htmlFor="booking-list_tn-commission-rate">Travelnest commission rate on {channelName}</FormLabel>
				<FormInputSymbol id={'booking-list_tn-commission-rate'}
					symbol="%"
					position="after"
					value={tnCommissionRate}
					placeholder={UNDEFINED} readOnly />
			</FormRow>
			<FormRow>
				<FormLabel htmlFor="booking-list_tn-commission-value">Travelnest commission value {tnVatRequiredText}</FormLabel>
				<FormInputSymbol id={'booking-list_tn-commission-value'}
					symbol={currencySymbol}
					value={tnCommissionValue}
					placeholder={UNDEFINED} readOnly />
			</FormRow>
			{
				breakdownSucceeded && <>
					<Rule />

					<h2>Payment</h2>
					<FormLabel htmlFor="booking-list_owner-income">Owner income after deductions</FormLabel>
					<FormInputSymbol id={'booking-list_owner-income'}
						symbol={currencySymbol}
						value={ownerPaymentValue || ownerPaymentValue === 0 ? ownerPaymentValue.toFixed(2) : undefined}
						placeholder={UNDEFINED} readOnly />
				</>
			}

			<Rule />
			<ExceptionalPayments event={props.event} />
		</React.Fragment >
	</Content >;
};
